import type { LiveCommerceReminder } from "@repo/api-client";
import { liveCommerceService } from "@repo/api-client";
import type { Reminder } from "../types/upcoming-live.types";
import { logReminderError, logReminderInfo, maskMobileNumber } from "./reminderLogger";

export interface ReminderServiceAdapter {
  getUserReminders: (payload: { mobileNo: string; influencerId?: string }) => Promise<{
    data?: { reminders: Reminder[] };
    message?: string;
  }>;
  setReminder: (payload: {
    mobileNo: string;
    influencerId: string;
    videoId: string;
  }) => Promise<{ data?: { reminder: Reminder }; message?: string }>;
  removeReminder: (payload: {
    mobileNo: string;
    videoId: string;
    influencerId: string;
  }) => Promise<{
    data?: { deleted: boolean };
    message?: string;
  }>;
}

function mapToReminder(r: LiveCommerceReminder, mobileNo: string): Reminder {
  return {
    id: r.id,
    videoId: r.videoId,
    influencerId: r.influencerId,
    isActive: r.isActive,
    createdAt: r.createdAt,
    updatedAt: r.updatedAt,
    mobileNo,
  };
}

export function createReminderService(): ReminderServiceAdapter {
  return {
    async getUserReminders({ mobileNo, influencerId }) {
      logReminderInfo("ReminderServiceAdapter", "getUserReminders:start", {
        mobileNoMasked: maskMobileNumber(mobileNo),
        influencerId,
      });

      try {
        const res = await liveCommerceService.getUserReminders({ mobileNo, influencerId });
        const reminders = (res.data?.reminders ?? []).map((r) => mapToReminder(r, mobileNo));

        logReminderInfo("ReminderServiceAdapter", "getUserReminders:response", {
          mobileNoMasked: maskMobileNumber(mobileNo),
          influencerId,
          message: res.message,
          reminderCount: reminders.length,
          activeReminderCount: reminders.filter((reminder) => reminder.isActive).length,
          response: res,
        });

        return { data: { reminders }, message: res.message };
      } catch (error) {
        logReminderError("ReminderServiceAdapter", "getUserReminders:error", error, {
          mobileNoMasked: maskMobileNumber(mobileNo),
          influencerId,
        });
        throw error;
      }
    },
    async setReminder(payload) {
      logReminderInfo("ReminderServiceAdapter", "setReminder:start", {
        influencerId: payload.influencerId,
        videoId: payload.videoId,
        mobileNoMasked: maskMobileNumber(payload.mobileNo),
      });

      try {
        const res = await liveCommerceService.setReminder(payload);
        const reminder = res.data?.reminder
          ? mapToReminder(res.data.reminder, payload.mobileNo)
          : undefined;

        logReminderInfo("ReminderServiceAdapter", "setReminder:response", {
          influencerId: payload.influencerId,
          videoId: payload.videoId,
          mobileNoMasked: maskMobileNumber(payload.mobileNo),
          success: Boolean(reminder),
          reminderId: reminder?.id,
          message: res.message,
          response: res,
        });

        return { data: reminder ? { reminder } : undefined, message: res.message };
      } catch (error) {
        logReminderError("ReminderServiceAdapter", "setReminder:error", error, {
          influencerId: payload.influencerId,
          videoId: payload.videoId,
          mobileNoMasked: maskMobileNumber(payload.mobileNo),
        });
        throw error;
      }
    },
    async removeReminder(payload) {
      logReminderInfo("ReminderServiceAdapter", "removeReminder:start", {
        influencerId: payload.influencerId,
        videoId: payload.videoId,
        mobileNoMasked: maskMobileNumber(payload.mobileNo),
      });

      try {
        const res = await liveCommerceService.removeReminder(payload);

        logReminderInfo("ReminderServiceAdapter", "removeReminder:response", {
          influencerId: payload.influencerId,
          videoId: payload.videoId,
          mobileNoMasked: maskMobileNumber(payload.mobileNo),
          deleted: res.data?.deleted ?? false,
          message: res.message,
          response: res,
        });

        return {
          data: { deleted: res.data?.deleted ?? false },
          message: res.message,
        };
      } catch (error) {
        logReminderError("ReminderServiceAdapter", "removeReminder:error", error, {
          influencerId: payload.influencerId,
          videoId: payload.videoId,
          mobileNoMasked: maskMobileNumber(payload.mobileNo),
        });
        throw error;
      }
    },
  };
}

export const defaultReminderService = createReminderService();
